"use client";

import { useState } from "react";
import { SlidersHorizontal, X, Star, Calendar, Info, Navigation, ShoppingBag, BedDouble } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { InfoTooltip, TOOLTIPS } from "@/features/help";
import { useTheme } from "@/features/theme";

export interface FilterState {
  minRating: number;
  dateFrom: string;
  dateTo: string;
  hideHotspotTypes: string[];
}

const DEFAULT_FILTERS: FilterState = {
  minRating: 0,
  dateFrom: "",
  dateTo: "",
  hideHotspotTypes: [],
};

const HOTSPOT_TYPE_OPTIONS = [
  { type: "info",    label: "Info",    icon: Info,        color: "text-blue-400",    activeColor: "bg-blue-600/30 border-blue-400/50 text-blue-300" },
  { type: "tour",    label: "Tour",    icon: Navigation,  color: "text-purple-400",  activeColor: "bg-purple-600/30 border-purple-400/50 text-purple-300" },
  { type: "product", label: "Product", icon: ShoppingBag, color: "text-emerald-400", activeColor: "bg-emerald-600/30 border-emerald-400/50 text-emerald-300" },
  { type: "hotel",   label: "Hotel",   icon: BedDouble,   color: "text-orange-400",  activeColor: "bg-orange-600/30 border-orange-400/50 text-orange-300" },
];

// ── Shared panel props ────────────────────────────────────────────────────────

export interface FilterPanelProps {
  filters: FilterState;
  filteredCount: number;
  dirty: boolean;
  onChange: (f: FilterState) => void;
  onClose: () => void;
  isDark?: boolean;
}

// ── FilterPanel — pure panel (no trigger button) ──────────────────────────────

export function FilterPanel({ filters, filteredCount, dirty, onChange, onClose, isDark: isDarkProp }: FilterPanelProps) {
  const { isDark: themeIsDark } = useTheme();
  const isDark = isDarkProp ?? themeIsDark;
  return (
    <motion.div
      initial={{ opacity: 0, x: -10, scale: 0.97 }}
      animate={{ opacity: 1, x: 0,   scale: 1    }}
      exit={{   opacity: 0, x: -10, scale: 0.97 }}
      transition={{ type: "spring", stiffness: 340, damping: 28 }}
      className={`w-72 backdrop-blur-2xl border rounded-3xl p-6 shadow-2xl ${
        isDark
          ? "bg-black/80 border-white/10 text-white"
          : "bg-white/95 border-black/[0.08] text-neutral-900"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className={`flex items-center gap-2 font-black text-[10px] uppercase tracking-widest text-tone-400`}>
          <SlidersHorizontal className="w-3.5 h-3.5 text-indigo-400" />
          Advanced Filters
        </div>
        <button
          onClick={onClose}
          className={`p-1.5 rounded-full transition-all bg-ink/5 hover:bg-ink/10 text-ink/40 hover:text-ink`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {/* Min Rating */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <label className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink/70`}>
            <Star className="w-3.5 h-3.5 text-yellow-400" />
            Min Rating
          </label>
          <span className="text-sm font-bold text-yellow-500">
            {filters.minRating === 0 ? "Any" : `${filters.minRating}★`}
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={5}
          step={0.5}
          value={filters.minRating}
          onChange={(e) => onChange({ ...filters, minRating: Number(e.target.value) })}
          className={`w-full h-1 cursor-pointer appearance-none rounded-full accent-yellow-400 bg-ink/20`}
        />
        <div className={`flex justify-between text-[10px] mt-1 text-ink/30`}>
          <span>Any</span>
          <span>5★</span>
        </div>
      </div>

      {/* Date Range */}
      <div className="mb-5">
        <label className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-2 text-ink/70`}>
          <Calendar className="w-3.5 h-3.5 text-blue-400" />
          Capture Date Range
        </label>
        <div className="flex flex-col gap-2">
          <div>
            <span className={`text-[10px] mb-0.5 block text-ink/40`}>From</span>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => onChange({ ...filters, dateFrom: e.target.value })}
              className={`w-full border rounded-lg px-3 py-1.5 text-xs outline-none focus:border-indigo-500 transition-colors ${
                isDark
                  ? "bg-white/10 border-white/10 text-white [color-scheme:dark]"
                  : "bg-black/[0.04] border-black/10 text-neutral-900 [color-scheme:light]"
              }`}
            />
          </div>
          <div>
            <span className={`text-[10px] mb-0.5 block text-ink/40`}>To</span>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => onChange({ ...filters, dateTo: e.target.value })}
              className={`w-full border rounded-lg px-3 py-1.5 text-xs outline-none focus:border-indigo-500 transition-colors ${
                isDark
                  ? "bg-white/10 border-white/10 text-white [color-scheme:dark]"
                  : "bg-black/[0.04] border-black/10 text-neutral-900 [color-scheme:light]"
              }`}
            />
          </div>
        </div>
      </div>

      {/* Hide by Hotspot Type */}
      <div className="mb-5">
        <label className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-2 text-ink/70`}>
          <SlidersHorizontal className="w-3.5 h-3.5 text-indigo-400" />
          Hide photos with
          <InfoTooltip content={TOOLTIPS.filterHotspotTypes} variant="info" size="md" />
        </label>
        <div className="flex gap-2 flex-wrap">
          {HOTSPOT_TYPE_OPTIONS.map(({ type, label, icon: Icon, color, activeColor }) => {
            const hidden = filters.hideHotspotTypes.includes(type);
            const toggle = () => {
              const next = hidden
                ? filters.hideHotspotTypes.filter((t) => t !== type)
                : [...filters.hideHotspotTypes, type];
              onChange({ ...filters, hideHotspotTypes: next });
            };
            return (
              <button
                key={type}
                onClick={toggle}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-[11px] font-bold uppercase tracking-wider transition-all ${
                  hidden
                    ? activeColor
                    :"bg-ink/5 border-ink/10 text-ink/40 hover:text-ink hover:border-ink/25"
                }`}
              >
                <Icon className={`w-3 h-3 ${hidden ? "" : color}`} />
                {label}
              </button>
            );
          })}
        </div>
        {filters.hideHotspotTypes.length > 0 && (
          <p className={`text-[10px] mt-2 text-ink/30`}>
            Hiding panoramas that contain{" "}
            {filters.hideHotspotTypes.join(" or ")} hotspots
          </p>
        )}
      </div>

      {/* Clear */}
      {dirty && (
        <button
          onClick={() => onChange(DEFAULT_FILTERS)}
          className={`w-full py-2 rounded-xl border text-xs transition-all ${
            isDark
              ? "border-white/10 text-white/60 hover:bg-white/10"
              : "border-black/10 text-neutral-500 hover:bg-black/[0.05]"
          }`}
        >
          Clear all filters
        </button>
      )}
    </motion.div>
  );
}

// ── FilterSidebar — self-contained (button + panel) ───────────────────────────

interface FilterSidebarProps {
  totalCount: number;
  filteredCount: number;
  filters: FilterState;
  onChange: (f: FilterState) => void;
}

export default function FilterSidebar({
  totalCount,
  filteredCount,
  filters,
  onChange,
}: FilterSidebarProps) {
  const [open, setOpen] = useState(false);

  const dirty =
    filters.minRating > 0 || filters.dateFrom !== "" || filters.dateTo !== "" || filters.hideHotspotTypes.length > 0;

  return (
    <div className="relative z-[30] flex items-center gap-2 pointer-events-auto">
      <AnimatePresence>
        {open && (
          <div className="absolute bottom-[calc(100%+12px)] left-1/2 -translate-x-1/2">
            <FilterPanel
              filters={filters}
              filteredCount={filteredCount}
              dirty={dirty}
              onChange={onChange}
              onClose={() => setOpen(false)}
            />
          </div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center justify-center gap-2 min-w-[140px] px-5 py-2.5 rounded-2xl border backdrop-blur-2xl shadow-2xl text-[10px] font-black uppercase tracking-widest transition-colors
          ${open || dirty
            ? "bg-indigo-600 border-indigo-400 text-white shadow-indigo-500/20"
            : "bg-canvas/40 border-ink/10 text-tone-400 hover:text-ink hover:border-ink/25"
          }`}
      >
        <SlidersHorizontal className="w-3.5 h-3.5 shrink-0" />
        <span>{dirty ? "Filtering Active" : "Filter"}</span>
        {dirty && (
          <span className="bg-ink/20 px-1.5 py-0.5 rounded text-[8px] shrink-0">
            {filteredCount}
          </span>
        )}
      </button>
    </div>
  );
}

export { DEFAULT_FILTERS };
